import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { API_MESSAGE_SINK, ApiMessageSink } from '../models/api-response.model';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { normalizePartialViewName, parsePartialViewConfig } from '../utils/partial-view-config.util';
import { environment } from '../../environments/environment';
import { getActiveLanguage } from '../../app/core/utils/active-language.util';
import {
  WidgetConfigurationApiResponse,
  WidgetConfigurationData,
  WidgetItem,
  GridConfigurationData,
  GridConfigurationColumn,
  ViewOption,
  PartialViewOption,
  UpdateWidgetItem,
  UpdateWidgetConfigurationResponse,
  UpdatePartialViewConfigResponse,
  UpdateGridConfigurationRequest,
  UpdateGridConfigurationResponse,
  ExtraInfoVisibilityItem,
  UpdateExtraInfoVisibilityRequest,
  UpdateExtraInfoVisibilityResponse
} from './widget-configuration.types';

/** Normalize a raw widget from API (camelCase or PascalCase) to WidgetItem so displayOrder etc. are always correct. */
function normalizeWidget(raw: Record<string, unknown>): WidgetItem {
  const get = (camel: string, pascal: string) =>
    (raw[camel] ?? raw[pascal]) as number | string | Record<string, string> | undefined;
  return {
    widgetId: Number(get('widgetId', 'WidgetId')) || 0,
    name: String(get('name', 'Name') ?? ''),
    controlType: String(get('controlType', 'ControlType') ?? ''),
    displayOrder: Number(get('displayOrder', 'DisplayOrder')) ?? 0,
    displayStatus: Number(get('displayStatus', 'DisplayStatus')) ?? 0,
    metadata: get('metadata', 'Metadata') as Record<string, string> | undefined,
  };
}

@Injectable({
  providedIn: 'root'
})
export class WidgetConfigurationService {
  private readonly baseUrl = `${environment.apiBaseUrl}/WidgetConfiguration`;

  /** In-memory cache for getWidgetConfiguration; invalidated on update or via clearWidgetConfigurationCache. */
  private widgetConfigCache = new Map<string, WidgetConfigurationData | null>();

  constructor(private readonly http: HttpClient) {}

  /**
   * GET widget configuration for the given page path (all widgets). Filter by display status on the client if needed.
   * Results are cached per path + language; cache is cleared when that path is updated or via clearWidgetConfigurationCache.
   * @param path API path segment (e.g. 'MemberDetails/PersonalInformation'). No leading slash.
   * @param forceRefresh If true, bypass cache and refetch from API
   * @param language Optional language code (e.g. en, tr) for translated metadata. When provided, API returns labels/placeholders in that language.
   * Returns widgets array sorted by displayOrder.
   */
  getWidgetConfiguration(path: string, forceRefresh = false, language?: string | null): Observable<WidgetConfigurationData | null> {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const requestedLang = language && language.trim() ? language.trim().toLowerCase() : '';
    // Without an explicit language the API still translates by Accept-Language (http.interceptor.ts),
    // so the response is language-specific either way and the cache key has to record which language
    // it holds. Keying on the path alone served the previous language's labels after a switch.
    const cacheKey = `${normalizedPath}|${requestedLang || getActiveLanguage()}`;

    if (!forceRefresh && this.widgetConfigCache.has(cacheKey)) {
      return of(this.widgetConfigCache.get(cacheKey)!);
    }

    const url = requestedLang
      ? `${this.baseUrl}/${normalizedPath}?language=${encodeURIComponent(requestedLang)}`
      : `${this.baseUrl}/${normalizedPath}`;
    return this.http.get<WidgetConfigurationApiResponse>(url).pipe(
      map((res): WidgetConfigurationData | null => {
        const rawWidgets = (res as any)?.widgets ?? (res as any)?.Widgets;
        if (!rawWidgets?.length) {
          return null;
        }
        const widgets = (Array.isArray(rawWidgets) ? rawWidgets : [])
          .map((w: Record<string, unknown>) => normalizeWidget(w))
          .sort((a, b) => a.displayOrder - b.displayOrder);
        const viewName = (res as any).viewName ?? (res as any).ViewName ?? '';
        const partialViewName = (res as any).partialViewName ?? (res as any).PartialViewName ?? '';
        const partialViewId = Number((res as any).partialViewId ?? (res as any).PartialViewId ?? 0);
        return {
          viewName,
          partialViewName,
          partialViewId,
          widgets
        };
      }),
      tap((result) => this.widgetConfigCache.set(cacheKey, result)),
      catchError(() => of(null))
    );
  }

  /**
   * Clear cached widget configuration for a path (or all). Call after update or when data may be stale.
   * @param path If provided, clear only this path (e.g. 'MemberDetails/PersonalInformation'); otherwise clear all.
   */
  clearWidgetConfigurationCache(path?: string): void {
    if (path != null) {
      const normalized = path.startsWith('/') ? path.slice(1) : path;
      for (const key of this.widgetConfigCache.keys()) {
        if (key === normalized || key.startsWith(normalized + '|')) this.widgetConfigCache.delete(key);
      }
    } else {
      this.widgetConfigCache.clear();
    }
  }

  /**
   * GET list of views (for dropdown).
   * API: GET /api/WidgetConfiguration/Views
   */
  getViews(): Observable<ViewOption[]> {
    const url = `${this.baseUrl}/Views`;
    return this.http.get<unknown>(url).pipe(
      map((list) => {
        const arr = Array.isArray(list) ? list : [];
        return arr.map((x: Record<string, unknown>) => ({
          viewId: (x['viewId'] ?? x['ViewId']) as number,
          name: String(x['name'] ?? x['Name'] ?? ''),
          nameKey: String(x['nameKey'] ?? x['NameKey'] ?? x['name'] ?? x['Name'] ?? '')
        }));
      }),
      catchError(() => of([]))
    );
  }

  /**
   * GET list of partial views, optionally filtered by view name.
   * API: GET /api/WidgetConfiguration/PartialViews?viewName=...
   */
  getPartialViews(viewName?: string): Observable<PartialViewOption[]> {
    const url = viewName
      ? `${this.baseUrl}/PartialViews?viewName=${encodeURIComponent(viewName)}`
      : `${this.baseUrl}/PartialViews`;
    return this.http.get<unknown>(url).pipe(
      map((list) => {
        const arr = Array.isArray(list) ? list : [];
        return arr.map((x: Record<string, unknown>) => ({
          partialViewId: (x['partialViewId'] ?? x['PartialViewId']) as number,
          name: String(x['name'] ?? x['Name'] ?? ''),
          nameKey: String(x['nameKey'] ?? x['NameKey'] ?? x['name'] ?? x['Name'] ?? ''),
          viewPermissionId: (x['viewPermissionId'] ?? x['ViewPermissionId']) as number | null | undefined,
          editPermissionId: (x['editPermissionId'] ?? x['EditPermissionId']) as number | null | undefined,
          type: (x['type'] ?? x['Type']) as string | null | undefined,
          clientId: (x['clientId'] ?? x['ClientId']) as number,
          config: this.configToString(x['config'] ?? x['Config'])
        }));
      }),
      catchError(() => of([]))
    );
  }

  /** Cached "inactive partial views" per view name; cleared when a partial view config is saved. */
  private inactivePartialViewsCache = new Map<string, Observable<Set<string>>>();

  /**
   * Normalized names (lowercase, no spaces/_/-) of the partial views of `viewName` whose saved
   * Config has DisplayStatus inactive. Consumers hide those partial views from the page.
   * Fails open: if the list can't be loaded, nothing is reported inactive.
   */
  getInactivePartialViewNames(viewName: string): Observable<Set<string>> {
    let cached = this.inactivePartialViewsCache.get(viewName);
    if (!cached) {
      cached = this.getPartialViews(viewName).pipe(
        map((list) => new Set(
          list
            .filter((pv) => parsePartialViewConfig(pv.config).displayStatus === 0)
            .flatMap((pv) => [normalizePartialViewName(pv.nameKey), normalizePartialViewName(pv.name)])
        )),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.inactivePartialViewsCache.set(viewName, cached);
    }
    return cached;
  }

  /** The API may return Config as a JSON string or as an already-parsed object; keep it as a string. */
  private configToString(value: unknown): string | null {
    if (value == null) return null;
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  /**
   * PUT the partial view's Config (display status + label text key); the backend stores it as a JSON string.
   * API: PUT /api/WidgetConfiguration/PartialViews/{partialViewId}/Config
   * Body: { "Config": { "DisplayStatus": true, "LabelTextKey": "AccountInfo" } }
   */
  updatePartialViewConfig(partialViewId: number, config: Record<string, unknown>): Observable<UpdatePartialViewConfigResponse> {
    const url = `${this.baseUrl}/PartialViews/${partialViewId}/Config`;
    // Envelope `message` is a translation key (e.g. "[widgetConfigurationController.partialViewConfigUpdated]");
    // the interceptor unwraps it away, so capture it through the sink. `data` is the updated partial view.
    const sink: ApiMessageSink = {};
    const context = new HttpContext().set(API_MESSAGE_SINK, sink);
    return this.http.put<UpdatePartialViewConfigResponse>(url, { Config: config }, { context }).pipe(
      tap(() => this.inactivePartialViewsCache.clear()),
      map((data) => ({ ...data, apiMessage: sink.message })),
      catchError((error) => {
        // Failures arrive as the envelope in error.error (message is a key, e.g. "InvalidPartialViewId")
        // or as an ApiResponseError thrown by the unwrap interceptor.
        const message = error?.error?.message || (error?.name === 'ApiResponseError' ? error.message : null);
        return throwError(() => ({
          message: message || 'Failed to save partial view settings',
          statusCode: error?.status || error?.statusCode || 500,
          errorCode: error?.error?.errorCode ?? error?.errorCode,
          validationErrors: error?.error?.validationErrors ?? error?.validationErrors
        }));
      })
    );
  }

  /**
   * GET full widget configuration for the editor (all widgets, no displayStatus filter).
   * @param path API path segment (e.g. 'MemberDetails/PersonalInformation'). No leading slash.
   * @param language Optional language code for translated metadata.
   */
  getWidgetConfigurationForEditor(path: string, language?: string | null): Observable<WidgetConfigurationData | null> {
    return this.getWidgetConfiguration(path, false, language);
  }

  /**
   * PUT widget configuration updates. Invalidates the cache for this view/partial so next get returns fresh data.
   * API: PUT /api/WidgetConfiguration/{viewName}/{partialViewName}
   */
  updateWidgetConfiguration(
    viewName: string,
    partialViewName: string,
    body: { widgets: UpdateWidgetItem[] }
  ): Observable<UpdateWidgetConfigurationResponse> {
    const url = `${this.baseUrl}/${viewName}/${partialViewName}`;
    const path = `${viewName}/${partialViewName}`;
    return this.http.put<UpdateWidgetConfigurationResponse>(url, body).pipe(
      tap(() => this.clearWidgetConfigurationCache(path))
    );
  }

  /**
   * GET grid configuration by grid name.
   * API: GET /api/WidgetConfiguration/Grid/{gridName}?language=
   */
  getGridConfiguration(gridName: string, language?: string | null): Observable<GridConfigurationData | null> {
    const normalizedGridName = (gridName ?? '').trim();
    if (!normalizedGridName) {
      return of(null);
    }

    const lang = language && language.trim() ? language.trim().toLowerCase() : '';
    const url = lang
      ? `${this.baseUrl}/Grid/${encodeURIComponent(normalizedGridName)}?language=${encodeURIComponent(lang)}`
      : `${this.baseUrl}/Grid/${encodeURIComponent(normalizedGridName)}`;
    return this.http.get<unknown>(url).pipe(
      map((response) => {
        const raw = response as Record<string, unknown> | null;
        if (!raw) {
          return null;
        }

        const rawColumns = (raw['columns'] ?? raw['Columns']) as unknown;
        const columnsArray = Array.isArray(rawColumns) ? rawColumns : [];
        const columns: GridConfigurationColumn[] = columnsArray
          .map((col) => {
            const item = col as Record<string, unknown>;
            return {
              gridConfigId: Number(item['gridConfigId'] ?? item['GridConfigId'] ?? 0) || undefined,
              columnName: String(item['columnName'] ?? item['ColumnName'] ?? ''),
              translatedColumnName: String(
                item['translatedColumnName'] ?? item['TranslatedColumnName'] ?? ''
              ).trim() || undefined,
              databinding: String(item['databinding'] ?? item['Databinding'] ?? ''),
              displayOrder: Number(item['displayOrder'] ?? item['DisplayOrder'] ?? 0),
              displayStatus: Number(item['displayStatus'] ?? item['DisplayStatus'] ?? 1)
            };
          })
          .filter((c) => !!c.databinding)
          .sort((a, b) => a.displayOrder - b.displayOrder);

        if (!columns.length) {
          return null;
        }

        return {
          gridName: String(raw['gridName'] ?? raw['GridName'] ?? normalizedGridName),
          columns
        } as GridConfigurationData;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * PUT grid configuration updates for a given grid name.
   * API: PUT /api/WidgetConfiguration/Grid/{gridName}
   */
  updateGridConfiguration(
    gridName: string,
    body: UpdateGridConfigurationRequest
  ): Observable<UpdateGridConfigurationResponse> {
    const normalizedGridName = (gridName ?? '').trim();
    const url = `${this.baseUrl}/Grid/${encodeURIComponent(normalizedGridName)}`;
    return this.http.put<UpdateGridConfigurationResponse>(url, body);
  }

  /**
   * GET top-level extra-info keys with their Active/Inactive status for the "Extra Info" partial view.
   * API: GET /api/WidgetConfiguration/ExtraInfoVisibility
   */
  getExtraInfoVisibility(): Observable<ExtraInfoVisibilityItem[]> {
    const url = `${this.baseUrl}/ExtraInfoVisibility`;
    return this.http.get<unknown>(url).pipe(
      map((response) => {
        const raw = response as Record<string, unknown> | null;
        const rawItems = (raw?.['items'] ?? raw?.['Items']) as unknown;
        const arr = Array.isArray(rawItems) ? rawItems : [];
        return arr.map((x: Record<string, unknown>) => ({
          name: String(x['name'] ?? x['Name'] ?? ''),
          displayStatus: Number(x['displayStatus'] ?? x['DisplayStatus'] ?? 0)
        })).filter((i) => !!i.name);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * PUT Active/Inactive status for top-level extra-info keys. Nested rules are preserved server-side.
   * API: PUT /api/WidgetConfiguration/ExtraInfoVisibility
   */
  updateExtraInfoVisibility(
    body: UpdateExtraInfoVisibilityRequest
  ): Observable<UpdateExtraInfoVisibilityResponse> {
    const url = `${this.baseUrl}/ExtraInfoVisibility`;
    return this.http.put<UpdateExtraInfoVisibilityResponse>(url, body);
  }
}
